import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  bottomTabItems,
  dashboardNavItems,
  equityDashboardNavItems,
  featureNavItems,
  getTopbarTitle,
  isDashboardRoute,
} from '../config/navigation';
import { captureEvent } from '../lib/analytics';
import { isDailyPulseEnabled } from '../lib/dailyPulseAvailability';
import TalkToHumanButton from './TalkToHumanButton';
import {
  canAccessFedWatchFeature,
  canAccessMarketContextFeature,
  canAccessThemesFeature,
  canAccessTradeLogFeature,
  canAccessWeekendMoversFeature,
} from '../lib/featureFlags';
import NotificationsSettingsModal from './notifications/NotificationsSettingsModal';

interface HeaderProps {
  showLoginButton?: boolean;
  hideNavigation?: boolean;
  theme?: 'light' | 'dark';
  onToggleTheme?: () => void;
}

const Header: React.FC<HeaderProps> = ({
  showLoginButton = true,
  hideNavigation = false,
  theme = 'light',
  onToggleTheme,
}) => {
  const { userEmail, userName, logout, authReady, isPremium } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [crossAssetOpen, setCrossAssetOpen] = useState(true);
  const [equitiesOpen, setEquitiesOpen] = useState(true);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const pathname = location.pathname;
  const dashboardPath = isDashboardRoute(pathname);
  const topbarTitle = getTopbarTitle(pathname);
  const dailyPulseEnabled = isDailyPulseEnabled();
  const canAccessTradeLog = canAccessTradeLogFeature(userEmail);
  const canAccessMarketContext = canAccessMarketContextFeature(userEmail);
  const canAccessFedWatch = canAccessFedWatchFeature(userEmail);
  const canAccessThemes = canAccessThemesFeature(userEmail);
  const canAccessWeekendMovers = canAccessWeekendMoversFeature(userEmail);
  const visibleFeatureNavItems = (dailyPulseEnabled
    ? featureNavItems
    : featureNavItems.filter((item) => item.path !== '/pulse')).filter((item) => {
    if (item.path === '/us-equities') return canAccessThemes;
    if (item.path === '/expected-return') return canAccessThemes;
    if (item.path === '/weekend-movers') return canAccessWeekendMovers;
    if (item.path === '/trade-log') return canAccessTradeLog;
    if (item.path === '/market-context') return canAccessMarketContext;
    if (item.path === '/fed-watch') return canAccessFedWatch;
    return true;
  });
  const visibleBottomTabItems = bottomTabItems.filter(
    (item) => {
      if (item.path === '/us-equities') return canAccessThemes;
      if (item.path === '/expected-return') return canAccessThemes;
      if (item.path === '/weekend-movers') return canAccessWeekendMovers;
      if (item.path === '/trade-log') return canAccessTradeLog;
      if (item.path === '/market-context') return canAccessMarketContext;
      if (item.path === '/fed-watch') return canAccessFedWatch;
      return true;
    }
  );

  useEffect(() => {
    setDrawerOpen(false);
    if (pathname === '/' || pathname.startsWith('/dashboards/')) {
      setCrossAssetOpen(true);
    }
    if (pathname.startsWith('/dashboards/equities')) {
      setEquitiesOpen(true);
    }
  }, [pathname]);

  const handleLogout = () => {
    captureEvent('logout', {
      from_path: location.pathname
    });
    logout();
    navigate('/login');
  };

  const getInitials = (name: string | null, email: string | null): string => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase();
    }
    if (email) {
      return email[0].toUpperCase();
    }
    return 'U';
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname === '/dashboards/allocation-history';
    }
    if (path === '/pulse') {
      return ['/pulse', '/pulse-editorial-experiment', '/dailypulse', '/dailypulseold', '/newsletter'].includes(location.pathname);
    }
    if (path === '/rankings') {
      return ['/rankings', '/derby'].includes(location.pathname);
    }
    if (path === '/strategies') {
      return location.pathname === '/strategies' || location.pathname.startsWith('/strategies/');
    }
    if (path === '/us-equities') {
      return location.pathname === '/us-equities' || location.pathname.startsWith('/us-equities/');
    }
    if (path === '/expected-return') {
      return location.pathname === '/expected-return' || location.pathname.startsWith('/expected-return/');
    }
    return location.pathname === path;
  };

  const isBottomTabActive = (path: string) => {
    if (path === '/') {
      return dashboardPath;
    }
    return isActive(path);
  };

  const navItemClass = (active: boolean) =>
    `flex min-h-[38px] items-center gap-2.5 rounded-[10px] px-3 py-2 text-[13px] font-medium transition-colors ${
      active ? 'bg-[var(--qcl-accent-light)] text-[var(--qcl-accent)]' : 'text-[var(--qcl-text-dim)] hover:bg-[var(--qcl-bg)] hover:text-[var(--qcl-text)]'
    }`;

  const iconClass = (active: boolean) =>
    `flex h-7 w-7 items-center justify-center rounded-[8px] text-sm ${
      active ? 'bg-[rgba(67,97,238,0.12)]' : 'bg-[var(--qcl-bg)]'
    }`;

  const renderNavItem = (item: { path: string; icon: string; label: string }) => {
    const active = isActive(item.path);
    return (
      <NavLink key={item.path} to={item.path} className={navItemClass(active)}>
        <span className={iconClass(active)}>{item.icon}</span>
        <span>{item.label}</span>
      </NavLink>
    );
  };

  const crossAssetRootItem = dashboardNavItems[0];
  const crossAssetChildItems = dashboardNavItems.slice(1);
  const crossAssetGroupActive = pathname === '/' || pathname.startsWith('/dashboards/');
  const equitiesGroupActive = pathname.startsWith('/dashboards/equities');
  const talkToHumanButtonClass =
    'flex w-full min-h-[38px] items-center gap-2.5 rounded-[10px] px-3 py-2 text-[13px] font-medium text-[var(--qcl-text-dim)] transition-colors hover:bg-[var(--qcl-bg)] hover:text-[var(--qcl-text)]';
  const themeLabel = theme === 'dark' ? 'Dark' : 'Light';

  return (
    <>
      {!hideNavigation && (
        <aside className="fixed inset-y-0 left-0 z-40 hidden w-[232px] border-r border-[var(--qcl-border)] bg-white md:block">
          <div className="flex h-full flex-col">
            <div className="flex-1 overflow-y-auto">
              <div className="border-b border-[var(--qcl-border)] px-[18px] pb-4 pt-5">
                <Link to="/" className="flex items-center gap-2">
                  <img
                    src="/favicon.png"
                    alt="QuantCheck Labs"
                    className="h-6 w-6 rounded-md object-contain"
                  />
                  <span className="text-[15px] font-extrabold tracking-[-0.02em] text-[var(--qcl-text)]">
                    QuantCheck <span className="font-medium text-[var(--qcl-text-dim)]">Labs</span>
                  </span>
                </Link>
              </div>

              <div className="px-3 pt-3">
                <p className="px-2 pb-1.5 text-[10px] font-bold uppercase tracking-[1.2px] text-[var(--qcl-text-muted)]">
                  Features
                </p>
                <div className="space-y-1">{visibleFeatureNavItems.map(renderNavItem)}</div>
              </div>

              <div className="mx-[18px] my-2 h-px bg-[var(--qcl-border)]" />

              <div className="px-3 pb-5">
                <p className="px-2 pb-1.5 text-[10px] font-bold uppercase tracking-[1.2px] text-[var(--qcl-text-muted)]">
                  Dashboards
                </p>
                <div className="space-y-1">
                  <div className={navItemClass(crossAssetGroupActive)}>
                    <NavLink to={crossAssetRootItem.path} className="flex min-w-0 flex-1 items-center gap-2.5">
                      <span className={iconClass(isActive(crossAssetRootItem.path))}>{crossAssetRootItem.icon}</span>
                      <span className="truncate">{crossAssetRootItem.label}</span>
                    </NavLink>
                    <button
                      type="button"
                      className="rounded-[8px] p-1 text-[10px] text-[var(--qcl-text-muted)] transition-colors hover:bg-[var(--qcl-bg)]"
                      onClick={() => setCrossAssetOpen((prev) => !prev)}
                      aria-label={crossAssetOpen ? 'Collapse cross-asset dashboards' : 'Expand cross-asset dashboards'}
                      aria-expanded={crossAssetOpen}
                    >
                      <span className={`block transition-transform ${crossAssetOpen ? 'rotate-90' : ''}`}>›</span>
                    </button>
                  </div>
                  <div className={`overflow-hidden pl-4 transition-all ${crossAssetOpen ? 'max-h-[520px]' : 'max-h-0'}`}>
                    <div className="space-y-1 pt-1">
                      {crossAssetChildItems.map(renderNavItem)}
                      <button
                        type="button"
                        className={navItemClass(equitiesGroupActive)}
                        onClick={() => setEquitiesOpen((prev) => !prev)}
                        aria-label={equitiesOpen ? 'Collapse equities dashboards' : 'Expand equities dashboards'}
                        aria-expanded={equitiesOpen}
                      >
                        <span className={iconClass(equitiesGroupActive)}>📈</span>
                        <span className="flex-1 text-left">Equities</span>
                        <span
                          className={`text-[10px] text-[var(--qcl-text-muted)] transition-transform ${
                            equitiesOpen ? 'rotate-90' : ''
                          }`}
                        >
                          ›
                        </span>
                      </button>
                      <div className={`overflow-hidden pl-4 transition-all ${equitiesOpen ? 'max-h-52' : 'max-h-0'}`}>
                        <div className="space-y-1 pt-1">
                          {equityDashboardNavItems.map(renderNavItem)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-[var(--qcl-border)] p-3">
              <TalkToHumanButton className={talkToHumanButtonClass} prefillEmail={userEmail} />
            </div>
          </div>
        </aside>
      )}

      <header
        className={`fixed right-0 top-0 z-30 h-14 border-b border-[var(--qcl-border)] bg-white ${
          hideNavigation ? 'left-0' : 'left-0 md:left-[232px]'
        }`}
      >
        <div className="flex h-full items-center justify-between px-4 md:px-7">
          <div className="flex items-center gap-3">
            {!hideNavigation && (
              <button
                type="button"
                className="flex h-11 w-11 items-center justify-center rounded-[10px] text-xl text-[var(--qcl-text)] md:hidden"
                onClick={() => setDrawerOpen((prev) => !prev)}
                aria-label="Toggle navigation menu"
              >
                ☰
              </button>
            )}
            <div className="text-base font-extrabold tracking-[-0.03em] text-[var(--qcl-text)] md:text-[19px]">
              {topbarTitle}
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <button
              type="button"
              onClick={onToggleTheme}
              className="inline-flex min-h-[34px] items-center gap-1.5 rounded-[8px] border border-[var(--qcl-border)] bg-[var(--qcl-bg)] px-2.5 py-1.5 text-[12px] font-semibold text-[var(--qcl-text-dim)] transition hover:text-[var(--qcl-text)]"
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              <span aria-hidden>{theme === 'dark' ? '🌙' : '☀️'}</span>
              <span className="hidden sm:inline">{themeLabel}</span>
            </button>
            {showLoginButton && authReady && !userEmail ? (
              <Button type="button" variant="outline" size="sm" onClick={() => navigate('/login')}>
                Log in
              </Button>
            ) : authReady && userEmail && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src="" alt={userName || userEmail} />
                      <AvatarFallback>{getInitials(userName, userEmail)}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{userName || 'User'}</p>
                      <p className="text-xs leading-none text-muted-foreground">{userEmail}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {isPremium ? (
                    <DropdownMenuItem onClick={() => setNotificationsOpen(true)}>Notifications</DropdownMenuItem>
                  ) : null}
                  <DropdownMenuItem onClick={handleLogout}>Log out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </header>

      <NotificationsSettingsModal open={notificationsOpen} onClose={() => setNotificationsOpen(false)} />

      {!hideNavigation && dashboardPath && (
        <div className="fixed left-0 right-0 top-14 z-20 border-b border-[var(--qcl-border)] bg-white md:hidden">
          <div className="relative">
            <div className="flex gap-1 overflow-x-auto px-3 py-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {dashboardNavItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`flex min-h-[34px] flex-none items-center gap-1 rounded-lg border px-3 py-1.5 text-xs font-semibold ${
                    isActive(item.path)
                      ? 'border-[rgba(67,97,238,0.2)] bg-[var(--qcl-accent-light)] text-[var(--qcl-accent)]'
                      : 'border-transparent text-[var(--qcl-text-dim)]'
                  }`}
                >
                  <span>{item.icon}</span>
                  <span>{item.shortLabel || item.label}</span>
                </NavLink>
              ))}
              <div className="my-auto h-5 w-px flex-none bg-[var(--qcl-border)]" />
              {equityDashboardNavItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`flex min-h-[34px] flex-none items-center gap-1 rounded-lg border px-3 py-1.5 text-xs font-semibold ${
                    isActive(item.path)
                      ? 'border-[rgba(67,97,238,0.2)] bg-[var(--qcl-accent-light)] text-[var(--qcl-accent)]'
                      : 'border-transparent text-[var(--qcl-text-dim)]'
                  }`}
                >
                  <span>{item.icon}</span>
                  <span>{item.shortLabel || item.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}

      {!hideNavigation && (
        <>
          <div
            className={`fixed inset-0 z-40 bg-black/25 backdrop-blur-[2px] transition-opacity md:hidden ${
              drawerOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
            }`}
            onClick={() => setDrawerOpen(false)}
          />
          <nav
            className={`fixed left-0 top-0 z-50 flex h-screen w-[290px] flex-col bg-white shadow-[4px_0_24px_rgba(0,0,0,0.08)] transition-transform md:hidden ${
              drawerOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
          >
            <div className="flex-1 overflow-y-auto">
              <div className="border-b border-[var(--qcl-border)] px-[18px] pb-4 pt-5">
                <Link to="/" className="flex items-center gap-2" onClick={() => setDrawerOpen(false)}>
                  <img
                    src="/favicon.png"
                    alt="QuantCheck Labs"
                    className="h-6 w-6 rounded-md object-contain"
                  />
                  <span className="text-[15px] font-extrabold tracking-[-0.02em] text-[var(--qcl-text)]">
                    QuantCheck <span className="font-medium text-[var(--qcl-text-dim)]">Labs</span>
                  </span>
                </Link>
              </div>

              <div className="px-3 pt-3">
                <p className="px-2 pb-1.5 text-[10px] font-bold uppercase tracking-[1.2px] text-[var(--qcl-text-muted)]">
                  Features
                </p>
                <div className="space-y-1">
                  {visibleFeatureNavItems.map((item) => (
                    <NavLink key={item.path} to={item.path} className={navItemClass(isActive(item.path))} onClick={() => setDrawerOpen(false)}>
                      <span className={iconClass(isActive(item.path))}>{item.icon}</span>
                      <span>{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>

              <div className="mx-[18px] my-2 h-px bg-[var(--qcl-border)]" />

              <div className="px-3 pb-5">
                <p className="px-2 pb-1.5 text-[10px] font-bold uppercase tracking-[1.2px] text-[var(--qcl-text-muted)]">
                  Dashboards
                </p>
                <div className="space-y-1">
                  <div className={navItemClass(crossAssetGroupActive)}>
                    <NavLink
                      to={crossAssetRootItem.path}
                      className="flex min-w-0 flex-1 items-center gap-2.5"
                      onClick={() => setDrawerOpen(false)}
                    >
                      <span className={iconClass(isActive(crossAssetRootItem.path))}>{crossAssetRootItem.icon}</span>
                      <span className="truncate">{crossAssetRootItem.label}</span>
                    </NavLink>
                    <button
                      type="button"
                      className="rounded-[8px] p-1 text-[10px] text-[var(--qcl-text-muted)] transition-colors hover:bg-[var(--qcl-bg)]"
                      onClick={() => setCrossAssetOpen((prev) => !prev)}
                      aria-label={crossAssetOpen ? 'Collapse cross-asset dashboards' : 'Expand cross-asset dashboards'}
                      aria-expanded={crossAssetOpen}
                    >
                      <span className={`block transition-transform ${crossAssetOpen ? 'rotate-90' : ''}`}>›</span>
                    </button>
                  </div>
                  <div className={`overflow-hidden pl-4 transition-all ${crossAssetOpen ? 'max-h-[520px]' : 'max-h-0'}`}>
                    <div className="space-y-1 pt-1">
                      {crossAssetChildItems.map((item) => (
                        <NavLink key={item.path} to={item.path} className={navItemClass(isActive(item.path))} onClick={() => setDrawerOpen(false)}>
                          <span className={iconClass(isActive(item.path))}>{item.icon}</span>
                          <span>{item.label}</span>
                        </NavLink>
                      ))}
                      <button
                        type="button"
                        className={navItemClass(equitiesGroupActive)}
                        onClick={() => setEquitiesOpen((prev) => !prev)}
                        aria-label={equitiesOpen ? 'Collapse equities dashboards' : 'Expand equities dashboards'}
                        aria-expanded={equitiesOpen}
                      >
                        <span className={iconClass(equitiesGroupActive)}>📈</span>
                        <span className="flex-1 text-left">Equities</span>
                        <span
                          className={`text-[10px] text-[var(--qcl-text-muted)] transition-transform ${
                            equitiesOpen ? 'rotate-90' : ''
                          }`}
                        >
                          ›
                        </span>
                      </button>
                      <div className={`overflow-hidden pl-4 transition-all ${equitiesOpen ? 'max-h-52' : 'max-h-0'}`}>
                        <div className="space-y-1 pt-1">
                          {equityDashboardNavItems.map((item) => (
                            <NavLink key={item.path} to={item.path} className={navItemClass(isActive(item.path))} onClick={() => setDrawerOpen(false)}>
                              <span className={iconClass(isActive(item.path))}>{item.icon}</span>
                              <span>{item.label}</span>
                            </NavLink>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-[var(--qcl-border)] p-3 pb-[calc(env(safe-area-inset-bottom,14px))]">
              <TalkToHumanButton
                className={talkToHumanButtonClass}
                prefillEmail={userEmail}
                onOpen={() => setDrawerOpen(false)}
              />
            </div>
          </nav>

          <nav
            aria-label="Primary mobile navigation"
            className="fixed bottom-0 left-0 right-0 z-30 border-t border-[var(--qcl-border)] bg-white/95 px-1 pb-[calc(env(safe-area-inset-bottom,14px))] pt-1 backdrop-blur-xl md:hidden"
          >
            <div className="flex items-center justify-around">
              {visibleBottomTabItems.map((item) => {
                const active = isBottomTabActive(item.path);
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={`flex min-h-[44px] flex-1 flex-col items-center justify-center rounded-lg px-1 py-1 text-[9px] font-semibold ${
                      active ? 'text-[var(--qcl-accent)]' : 'text-[var(--qcl-text-muted)]'
                    }`}
                  >
                    <span className="flex h-[22px] items-center text-[18px]">{item.icon}</span>
                    <span className="text-center leading-[1.05]">{item.shortLabel || item.label}</span>
                    <span
                      className={`mt-[1px] h-1 w-1 rounded-full bg-[var(--qcl-accent)] ${
                        active ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                  </NavLink>
                );
              })}
            </div>
          </nav>
        </>
      )}
    </>
  );
};

export default Header; 
